import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Label } from "@/components/ui/label";
import { ManualEntryForm } from "./ManualEntryForm";
import { ResearchResults } from "./ResearchResults";
import { DickinsonCountyScraper } from "./DickinsonCountyScraper";
import { CsvImport } from "./CsvImport";
import { DickinsonTextParser } from "./DickinsonTextParser";
import { AllenCountyTextParser } from "./AllenCountyTextParser";
import { LorainCountyTextParser } from "./LorainCountyTextParser";
import { MedinaCountyTextParser } from "./MedinaCountyTextParser";
import { MercerCountyTextParser } from "./MercerCountyTextParser";
import { MuskingumCountyTextParser } from "./MuskingumCountyTextParser";
import { PortageCountyTextParser } from "./PortageCountyTextParser";
import { ComalCountyTextParser } from "./ComalCountyTextParser";
import { WashtenawCountyTextParser } from "./WashtenawCountyTextParser";
import { NewaygoCountyTextParser } from "./NewaygoCountyTextParser";
import { CountyFusionTextParser } from "./CountyFusionTextParser";
import { CuyahogaCountyTextParser } from "./CuyahogaCountyTextParser";
import { WarrenCountyTextParser } from "./WarrenCountyTextParser";
import { WhatcomCountyTextParser } from "./WhatcomCountyTextParser";
import { WhatcomIndexParser } from "./WhatcomIndexParser";
import { LivingstonCountyTextParser } from "./LivingstonCountyTextParser";
import { CoryellCountyTextParser } from "./CoryellCountyTextParser";
import { LapeerCountyTextParser } from "./LapeerCountyTextParser";
import { BrewsterCountyTextParser } from "./BrewsterCountyTextParser";
import { CollinCountyTextParser } from "./CollinCountyTextParser";
import { DallasCountyTextParser } from "./DallasCountyTextParser";
import { AntrimCountyTextParser } from "./AntrimCountyTextParser";
import { ShermanCountyTextParser } from "./ShermanCountyTextParser";
import { FanninCountyTextParser } from "./FanninCountyTextParser";
import { BrownCountyTextParser } from "./BrownCountyTextParser";
import { UnionCountyTextParser } from "./UnionCountyTextParser";
import { QuickCaptureMode } from "./QuickCaptureMode";
import { CourthouseSheetScanner } from "./CourthouseSheetScanner";
import { DirectScreenshotCapture } from "./DirectScreenshotCapture";
import { DataExportTool } from "./DataExportTool";
import { AddressWorksheet } from "./AddressWorksheet";
import { WorksheetScanner } from "./WorksheetScanner";
import type { SearchResults } from "./types";

// CountyFusion/GovOS counties - these all use the same parser
const COUNTY_FUSION_COUNTIES = [
  'ashland', 'auglaize', 'belmont', 'butler', 'carroll', 'champaign', 
  'clark', 'clermont', 'coshocton', 'fayette', 'franklin', 'fulton', 
  'greene', 'guernsey', 'hancock', 'hardin', 'harrison', 'henry', 
  'highland', 'jefferson', 'licking', 'logan', 'monroe', 'morgan', 
  'ottawa', 'pickaway', 'pike', 'preble', 'richland', 'ross', 
  'sandusky', 'seneca', 'shelby', 'stark', 'tuscarawas', 'vanwert', 
  'washington', 'wayne'
];

// Texas ROAM counties
const TEXAS_ROAM_COUNTIES = ['comal', 'guadalupe', 'kendall', 'kerr', 'bandera'];

const COUNTIES = [
  { value: 'allen', label: 'Allen County, OH' },
  { value: 'antrim', label: 'Antrim County, MI', isNew: true },
  { value: 'ashland', label: 'Ashland County, OH' },
  { value: 'asotin', label: 'Asotin County, WA' },
  { value: 'auglaize', label: 'Auglaize County, OH' },
  { value: 'bandera', label: 'Bandera County, TX' },
  { value: 'barry', label: 'Barry County, MI', isNew: true },
  { value: 'bell', label: 'Bell County, TX', isNew: true },
  { value: 'belmont', label: 'Belmont County, OH' },
  { value: 'berrien', label: 'Berrien County, MI' },
  { value: 'blanco', label: 'Blanco County, TX', isNew: true },
  { value: 'brazos', label: 'Brazos County, TX' },
  { value: 'brewster', label: 'Brewster County, TX', isNew: true },
  { value: 'brown', label: 'Brown County, OH', isNew: true },
  { value: 'butler', label: 'Butler County, OH' },
  { value: 'cameron', label: 'Cameron County, TX', isNew: true },
  { value: 'calhoun', label: 'Calhoun County, MI' },
  { value: 'carroll', label: 'Carroll County, OH' },
  { value: 'champaign', label: 'Champaign County, OH' },
  { value: 'clark', label: 'Clark County, OH' },
  { value: 'clermont', label: 'Clermont County, OH' },
  { value: 'clinton', label: 'Clinton County, MI' },
  { value: 'collin', label: 'Collin County, TX', isNew: true },
  { value: 'comal', label: 'Comal County, TX' },
  { value: 'coryell', label: 'Coryell County, TX' },
  { value: 'coshocton', label: 'Coshocton County, OH' },
  { value: 'cuyahoga', label: 'Cuyahoga County, OH' },
  { value: 'dallas', label: 'Dallas County, TX', isNew: true },
  { value: 'dickinson', label: 'Dickinson County, MI' },
  { value: 'douglas', label: 'Douglas County, WA' },
  { value: 'eaton', label: 'Eaton County, MI' },
  { value: 'fayette', label: 'Fayette County, OH' },
  { value: 'fannin', label: 'Fannin County, TX', isNew: true },
  { value: 'ferry', label: 'Ferry County, WA' },
  { value: 'franklin', label: 'Franklin County, OH' },
  { value: 'fulton', label: 'Fulton County, OH' },
  { value: 'genesee', label: 'Genesee County, MI', isNew: true },
  { value: 'gillespie', label: 'Gillespie County, TX', isNew: true },
  { value: 'gratiot', label: 'Gratiot County, MI' },
  { value: 'goliad', label: 'Goliad County, TX', isNew: true },
  { value: 'greene', label: 'Greene County, OH' },
  { value: 'guadalupe', label: 'Guadalupe County, TX' },
  { value: 'hamilton', label: 'Hamilton County, OH', isNew: true },
  { value: 'guernsey', label: 'Guernsey County, OH' },
  { value: 'hancock', label: 'Hancock County, OH' },
  { value: 'hardin', label: 'Hardin County, OH' },
  { value: 'harrison', label: 'Harrison County, OH' },
  { value: 'henry', label: 'Henry County, OH' },
  { value: 'hidalgo', label: 'Hidalgo County, TX', isNew: true },
  { value: 'highland', label: 'Highland County, OH' },
  { value: 'hillsdale', label: 'Hillsdale County, MI' },
  { value: 'hockley', label: 'Hockley County, TX', isNew: true },
  { value: 'isabella', label: 'Isabella County, MI' },
  { value: 'jefferson', label: 'Jefferson County, OH' },
  { value: 'jimhogg', label: 'Jim Hogg County, TX', isNew: true },
  { value: 'kendall', label: 'Kendall County, TX' },
  { value: 'kerr', label: 'Kerr County, TX' },
  { value: 'kleberg', label: 'Kleberg County, TX', isNew: true },
  { value: 'lapeer', label: 'Lapeer County, MI' },
  { value: 'lawrence', label: 'Lawrence County, PA' },
  { value: 'lenawee', label: 'Lenawee County, MI' },
  { value: 'lewis', label: 'Lewis County, WA' },
  { value: 'lucas', label: 'Lucas County, OH', isNew: true },
  { value: 'licking', label: 'Licking County, OH' },
  { value: 'livingston', label: 'Livingston County, MI' },
  { value: 'logan', label: 'Logan County, OH' },
  { value: 'lorain', label: 'Lorain County, OH' },
  { value: 'madison', label: 'Madison County, OH' },
  { value: 'mecosta', label: 'Mecosta County, MI' },
  { value: 'montcalm', label: 'Montcalm County, MI' },
  { value: 'medina', label: 'Medina County, OH' },
  { value: 'mercer', label: 'Mercer County, OH' },
  { value: 'midland', label: 'Midland County, TX', isNew: true },
  { value: 'monroe', label: 'Monroe County, OH' },
  { value: 'morgan', label: 'Morgan County, OH' },
  { value: 'muskingum', label: 'Muskingum County, OH' },
  { value: 'newaygo', label: 'Newaygo County, MI' },
  { value: 'noble', label: 'Noble County, OH' },
  { value: 'nueces', label: 'Nueces County, TX', isNew: true },
  { value: 'ottawa', label: 'Ottawa County, OH' },
  { value: 'pickaway', label: 'Pickaway County, OH' },
  { value: 'pike', label: 'Pike County, OH' },
  { value: 'portage', label: 'Portage County, OH' },
  { value: 'potter', label: 'Potter County, TX', isNew: true },
  { value: 'preble', label: 'Preble County, OH' },
  { value: 'presqueisle', label: 'Presque Isle County, MI' },
  { value: 'randall', label: 'Randall County, TX' },
  { value: 'reagan', label: 'Reagan County, TX', isNew: true },
  { value: 'reeves', label: 'Reeves County, TX', isNew: true },
  { value: 'richland', label: 'Richland County, OH' },
  { value: 'ross', label: 'Ross County, OH' },
  { value: 'sandusky', label: 'Sandusky County, OH' },
  { value: 'sanjuan', label: 'San Juan County, WA' },
  { value: 'stclair', label: 'Saint Clair County, MI', isNew: true },
  { value: 'sanpatricio', label: 'San Patricio County, TX', isNew: true },
  { value: 'seneca', label: 'Seneca County, OH' },
  { value: 'shelby', label: 'Shelby County, OH' },
  { value: 'skagit', label: 'Skagit County, WA' },
  { value: 'sherman', label: 'Sherman County, TX', isNew: true },
  { value: 'stark', label: 'Stark County, OH' },
  { value: 'summit', label: 'Summit County, OH', isNew: true },
  { value: 'travis', label: 'Travis County, TX' },
  { value: 'tuscarawas', label: 'Tuscarawas County, OH' },
  { value: 'union', label: 'Union County, OH', isNew: true },
  { value: 'vanwert', label: 'Van Wert County, OH' },
  { value: 'warren', label: 'Warren County, OH' },
  { value: 'washtenaw', label: 'Washtenaw County, MI' },
  { value: 'washington', label: 'Washington County, OH' },
  { value: 'wayne', label: 'Wayne County, OH' },
  { value: 'wexford', label: 'Wexford County, MI' },
  { value: 'whatcom', label: 'Whatcom County, WA' },
  { value: 'williamson', label: 'Williamson County, TX' },
  { value: 'zapata', label: 'Zapata County, TX', isNew: true },
  { value: 'other', label: 'Other County' }
];

export function CountyResearchPage() {
  const [selectedCounty, setSelectedCounty] = useState('stark');
  const [activeTab, setActiveTab] = useState('import');
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const handleRecordSaved = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  const renderImportTab = () => {
    // Coryell County (unique format)
    if (selectedCounty === 'coryell') {
      return <CoryellCountyTextParser onRecordsSaved={handleRecordSaved} />;
    }

    // Brewster County (unique format)
    if (selectedCounty === 'brewster') {
      return <BrewsterCountyTextParser onRecordsSaved={handleRecordSaved} />;
    }

    // Brown County, OH (multi-line or/ee/AC format)
    if (selectedCounty === 'brown') {
      return <BrownCountyTextParser onRecordsSaved={handleRecordSaved} />;
    }

    // Collin / Gillespie / Goliad County (concatenated format)
    if (selectedCounty === 'collin' || selectedCounty === 'gillespie' || selectedCounty === 'goliad' || selectedCounty === 'cameron' || selectedCounty === 'nueces') {
      return <CollinCountyTextParser county={selectedCounty} onRecordsSaved={handleRecordSaved} />;
    }

    // Dallas County (tab-separated, same workflow as Collin)
    if (selectedCounty === 'dallas') {
      return <DallasCountyTextParser onRecordsSaved={handleRecordSaved} />;
    }

    // Texas ROAM counties
    if (TEXAS_ROAM_COUNTIES.includes(selectedCounty)) {
      return <ComalCountyTextParser county={selectedCounty} onRecordsSaved={handleRecordSaved} />;
    }

    // CountyFusion counties (38 Ohio counties)
    if (COUNTY_FUSION_COUNTIES.includes(selectedCounty)) {
      return <CountyFusionTextParser county={selectedCounty} onImportComplete={handleRecordSaved} />;
    }

    // Special parsers for specific counties
    switch (selectedCounty) {
      case 'dickinson':
        return <DickinsonTextParser onImportComplete={handleRecordSaved} />;
      case 'allen':
        return <AllenCountyTextParser onImportComplete={handleRecordSaved} />;
      case 'lorain':
        return <LorainCountyTextParser onImportComplete={handleRecordSaved} />;
      case 'medina':
        return <MedinaCountyTextParser onRecordsSaved={handleRecordSaved} />;
      case 'mercer':
        return <MercerCountyTextParser onRecordsSaved={handleRecordSaved} />;
      case 'muskingum':
        return <MuskingumCountyTextParser onRecordsSaved={handleRecordSaved} />;
      case 'portage':
        return <PortageCountyTextParser onRecordsSaved={handleRecordSaved} />;
      case 'washtenaw':
        return <WashtenawCountyTextParser />;
      case 'newaygo':
        return <NewaygoCountyTextParser onImportComplete={handleRecordSaved} />;
      case 'livingston':
      case 'stclair':
        return <LivingstonCountyTextParser onImportComplete={handleRecordSaved} />;
      case 'cuyahoga':
        return <CuyahogaCountyTextParser onRecordsSaved={handleRecordSaved} />;
      case 'warren':
        return <WarrenCountyTextParser onRecordsSaved={handleRecordSaved} />;
      case 'whatcom':
      case 'sanjuan':
      case 'asotin':
      case 'ferry':
        return (
          <div className="space-y-4">
            <WhatcomIndexParser onRecordsSaved={handleRecordSaved} county={selectedCounty} />
            <WhatcomCountyTextParser onRecordsSaved={handleRecordSaved} />
          </div>
        );
      case 'lapeer':
        return <LapeerCountyTextParser onImportComplete={handleRecordSaved} />;
      case 'antrim':
        return <AntrimCountyTextParser onImportComplete={handleRecordSaved} />;
      case 'sherman':
        return <ShermanCountyTextParser onRecordsSaved={handleRecordSaved} />;
      case 'fannin':
        return <FanninCountyTextParser onRecordsSaved={handleRecordSaved} />;
      case 'union':
        return <UnionCountyTextParser onRecordsSaved={handleRecordSaved} />;
      default:
        return <CsvImport onImportComplete={handleRecordSaved} />;
    }
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold">County Research</h1>
        <p className="text-muted-foreground mt-1">
          Unified interface for county property research across all counties
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Select County</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="w-full max-w-xs">
            <Label htmlFor="county-select">Working County</Label>
            <Select value={selectedCounty} onValueChange={setSelectedCounty}>
              <SelectTrigger id="county-select">
                <SelectValue placeholder="Select a county" />
              </SelectTrigger>
              <SelectContent>
                {COUNTIES.map(county => (
                  <SelectItem key={county.value} value={county.value}>
                    <span className="flex items-center gap-2">
                      {county.label}
                      {'isNew' in county && county.isNew && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400">
                          NEW
                        </span>
                      )}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList>
          <TabsTrigger value="import">📥 Import Data</TabsTrigger>
          <TabsTrigger value="screenshot-bot">🤖 Screenshot Bot</TabsTrigger>
          <TabsTrigger value="quick-capture">⚡ Quick Capture</TabsTrigger>
          <TabsTrigger value="manual">Manual Entry</TabsTrigger>
          <TabsTrigger value="automated">Automated Scraping</TabsTrigger>
          <TabsTrigger value="export">📊 Export Tool</TabsTrigger>
          <TabsTrigger value="scan-worksheet">📸 Scan Worksheet</TabsTrigger>
          <TabsTrigger value="results">View Records</TabsTrigger>
        </TabsList>

        <TabsContent value="import" className="space-y-4">
          {renderImportTab()}
        </TabsContent>

        <TabsContent value="screenshot-bot" className="space-y-4">
          <DirectScreenshotCapture 
            county={selectedCounty}
            onRecordSaved={handleRecordSaved}
          />
        </TabsContent>

        <TabsContent value="quick-capture" className="space-y-4">
          <QuickCaptureMode />
        </TabsContent>

        <TabsContent value="manual" className="space-y-4">
          <ManualEntryForm county={selectedCounty} onRecordSaved={handleRecordSaved} />
        </TabsContent>

        <TabsContent value="automated" className="space-y-4">
          {selectedCounty === 'dickinson' && (
            <DickinsonCountyScraper />
          )}
          {selectedCounty !== 'dickinson' && (
            <Card>
              <CardContent className="pt-6">
                <p className="text-muted-foreground text-center">
                  Automated scraping configuration for {COUNTIES.find(c => c.value === selectedCounty)?.label} coming soon.
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="export" className="space-y-4">
          <AddressWorksheet 
            county={selectedCounty} 
            countyLabel={COUNTIES.find(c => c.value === selectedCounty)?.label || selectedCounty} 
          />
          <DataExportTool />
        </TabsContent>

        <TabsContent value="scan-worksheet" className="space-y-4">
          <WorksheetScanner 
            county={selectedCounty}
            onRecordsUpdated={handleRecordSaved}
            onViewUpdatedRecords={() => {
              handleRecordSaved();
              setActiveTab('results');
            }}
          />
          <CourthouseSheetScanner
            county={selectedCounty}
            countyLabel={COUNTIES.find(c => c.value === selectedCounty)?.label || selectedCounty}
            onRecordsUpdated={handleRecordSaved}
            onViewUpdatedRecords={() => {
              handleRecordSaved();
              setActiveTab('results');
            }}
          />
        </TabsContent>

        <TabsContent value="results" className="space-y-4">
          <ResearchResults 
            searchResults={{
              records: [],
              isSearching: false,
              progress: 0,
              ownerFinanceCount: 0,
              addressesExtracted: 0
            }}
            onRefresh={handleRecordSaved}
            key={refreshTrigger}
            defaultCountyFilter={selectedCounty}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
